import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useCalculatorAnalytics from '../hooks/useCalculatorAnalytics';
import BRAND from '../config/brand';
import {
  DEFAULT_HOUSING_INPUTS,
  DEFAULT_TAX_ASSUMPTIONS,
  FILING_STATUS_OPTIONS,
  buildHousingDecisionProjection,
  defaultLoanStartDate,
  standardDeductionForStatus,
} from '../lib/housingDecisionCalculator';
import CalculatorDiscoveryCta from './CalculatorDiscoveryCta';
import ServiceNextStep from './ServiceNextStep';
import { CONTENT_CATEGORIES } from '../config/planningOffers';
import './IncomeChangePaycheckTaxEstimator.css';
import './RothTraditionalCalculator.css';
import './HousingDecisionCalculator.css';

const PAGE_TITLE = 'Rent vs Buy Housing Calculator | BPA';
const META_DESCRIPTION =
  "Compare buying, renting and investing, ownership costs, closing costs, and simplified tax assumptions with BPA's Rent vs Buy Housing Calculator.";

const LONG_DISCLOSURE =
  'This calculator is a general educational tool. It uses simplified assumptions for mortgage payments, property taxes, PMI, insurance, maintenance, closing costs, investment returns, rent increases, and tax deductions. Actual results vary by state, county, lender, loan type, tax law, income, filing status, insurance costs, market returns, and how long you own or rent. This is not personalized financial, tax, legal, mortgage, or investment advice.';

const TAX_DISCLOSURE =
  'Tax estimates use simplified assumptions (24% federal marginal rate, property tax in SALT with the current cap, and your filing status for the standard deduction). This is not a personal tax projection — a full housing scenario review belongs in a comprehensive financial plan, which starts with a Goal-Setting Meeting.';

const CALCULATOR_ID = 'housing_decision_calculator';
const CALCULATOR_NAME = 'housing_decision';

const ACCORDION_SECTIONS = [
  { id: 'home', title: '1. Home & Rent' },
  { id: 'mortgage', title: '2. Mortgage' },
  { id: 'ownership', title: '3. Ownership Costs' },
  { id: 'tax', title: '4. Tax Assumptions' },
];

function formatUsd(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatUsdCompact(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${Math.round(value)}`;
}

function formatDateLabel(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function usePageSeo(title, description) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute('content');
    meta.setAttribute('content', description);
    return () => {
      document.title = previousTitle;
      if (created) meta.remove();
      else if (previousDescription != null) meta.setAttribute('content', previousDescription);
    };
  }, [title, description]);
}

export default function HousingDecisionCalculator() {
  const {
    trackComplete,
    trackFieldInteraction,
    trackReset,
    trackResultView,
  } = useCalculatorAnalytics({
    calculatorId: CALCULATOR_ID,
    calculatorName: CALCULATOR_NAME,
  });

  usePageSeo(PAGE_TITLE, META_DESCRIPTION);

  const [values, setValues] = useState(() => ({
    ...DEFAULT_HOUSING_INPUTS,
    loanStartDate: defaultLoanStartDate(),
  }));
  const [resultsTab, setResultsTab] = useState('results');
  const [openSections, setOpenSections] = useState(() => new Set(['home', 'mortgage', 'ownership']));
  const [cashTouched, setCashTouched] = useState(false);

  const projection = useMemo(() => buildHousingDecisionProjection(values), [values]);

  useEffect(() => {
    if (!projection) return;
    const params = { result_category: projection.bestPath };
    trackResultView(params);
    trackComplete(params);
  }, [projection, trackComplete, trackResultView]);

  useEffect(() => {
    if (cashTouched) return;
    const home = Number(values.homeValue);
    const down = Number(values.downPayment);
    const closePct = Number(values.closingCostRate) / 100;
    if (!Number.isFinite(home) || !Number.isFinite(down) || !Number.isFinite(closePct)) return;
    const suggested = String(Math.round((down + home * closePct) * 100) / 100);
    setValues((current) => (
      current.availableCash === suggested ? current : { ...current, availableCash: suggested }
    ));
  }, [values.homeValue, values.downPayment, values.closingCostRate, cashTouched]);

  const updateField = (analyticsField, stateKey) => (value) => {
    trackFieldInteraction(analyticsField);
    if (stateKey === 'availableCash') setCashTouched(true);
    setValues((current) => {
      const next = { ...current, [stateKey]: value };
      if (stateKey === 'downPayment' || stateKey === 'homeValue') {
        const home = Number(stateKey === 'homeValue' ? value : next.homeValue);
        const down = Number(stateKey === 'downPayment' ? value : next.downPayment);
        if (
          Number.isFinite(home)
          && Number.isFinite(down)
          && home > 0
          && down / home < 0.2
          && (next.pmiRate === '0' || next.pmiRate === '')
        ) {
          next.pmiRate = '0.5';
        }
      }
      return next;
    });
  };

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resetCalculator = () => {
    setCashTouched(false);
    setValues({
      ...DEFAULT_HOUSING_INPUTS,
      loanStartDate: defaultLoanStartDate(),
    });
    setResultsTab('results');
    trackReset();
  };

  const loanPreview = useMemo(() => {
    const home = Number(values.homeValue);
    const down = Number(values.downPayment);
    if (!Number.isFinite(home) || !Number.isFinite(down) || home < 0 || down < 0) return null;
    return Math.max(0, Math.round((home - down) * 100) / 100);
  }, [values.homeValue, values.downPayment]);

  const closingPreview = useMemo(() => {
    const home = Number(values.homeValue);
    const rate = Number(values.closingCostRate) / 100;
    if (!Number.isFinite(home) || !Number.isFinite(rate)) return null;
    return Math.round(home * rate * 100) / 100;
  }, [values.homeValue, values.closingCostRate]);

  return (
    <div className="pete-page hdc-page">
      <header className="pete-header">
        <div className="pete-header-inner">
          <Link className="pete-logo-link" to="/links">
            <img
              src={BRAND.logoSrc}
              alt="Bautista Planning and Analytics"
              className="pete-logo-img"
              width={440}
              height={88}
              decoding="async"
            />
          </Link>
        </div>
      </header>

      <main className="pete-body">
        <div className="pete-inner hdc-inner">
          <div className="pete-title-block hdc-hero">
            <p className="hdc-brand-path">Clear Thinking → Better Decisions → The Golden Path</p>
            <h1>Rent vs Buy Housing Calculator</h1>
            <p className="hdc-hero-sub">
              Compare buying, renting and investing, down payment choices, ownership costs, and extra mortgage
              payments.
            </p>
            <p className="hdc-hero-question">
              Is it better to rent, buy, invest the down payment, make a larger down payment, or make extra
              mortgage payments?
            </p>
          </div>

          <div className="pete-disclaimer-banner hdc-disclaimer" role="note">
            <strong>Educational estimate only.</strong> {LONG_DISCLOSURE}
          </div>

          <div className="hdc-layout">
            <div className="hdc-assumptions">
              <div className="pete-card">
                <h2>Your Assumptions</h2>

                {ACCORDION_SECTIONS.map((section) => {
                  const open = openSections.has(section.id);
                  return (
                    <div className="hdc-accordion" key={section.id}>
                      <button
                        type="button"
                        className="hdc-accordion-trigger"
                        aria-expanded={open}
                        onClick={() => toggleSection(section.id)}
                      >
                        <span>{section.title}</span>
                        <span aria-hidden="true">{open ? '−' : '+'}</span>
                      </button>
                      {open ? (
                        <div className="hdc-accordion-body">
                          {section.id === 'home' ? (
                            <div className="rtc-input-grid">
                              <Field id="hdc-home-value" label="Home value" value={values.homeValue} onChange={updateField('home_value', 'homeValue')} step="1000" />
                              <Field id="hdc-down-payment" label="Down payment" value={values.downPayment} onChange={updateField('down_payment', 'downPayment')} step="1000" hint={loanPreview != null ? `Loan amount = ${formatUsd(loanPreview)}.` : undefined} />
                              <Field id="hdc-rent" label="Current monthly rent" value={values.monthlyRent} onChange={updateField('monthly_rent', 'monthlyRent')} step="50" />
                              <Field id="hdc-rent-increase" label="Annual rent increase (%)" value={values.annualRentIncrease} onChange={updateField('annual_rent_increase', 'annualRentIncrease')} step="0.25" hint="Applied once per year on the lease anniversary." />
                            </div>
                          ) : null}

                          {section.id === 'mortgage' ? (
                            <div className="rtc-input-grid">
                              <Field id="hdc-rate" label="Mortgage interest rate (%)" value={values.annualInterestRate} onChange={updateField('interest_rate', 'annualInterestRate')} step="0.125" />
                              <Field id="hdc-term" label="Loan term (months)" value={values.loanTermMonths} onChange={updateField('loan_term_months', 'loanTermMonths')} step="12" />
                              <Field id="hdc-start" label="Loan start date" value={values.loanStartDate} onChange={updateField('loan_start_date', 'loanStartDate')} type="date" />
                              <div className="rtc-calculated-field" aria-live="polite">
                                <span>Loan amount</span>
                                <strong>{loanPreview != null ? formatUsd(loanPreview) : '—'}</strong>
                                <small>Home value − down payment</small>
                              </div>
                              <Field id="hdc-appr" label="Annual home appreciation (%)" value={values.annualAppreciationRate} onChange={updateField('appreciation_rate', 'annualAppreciationRate')} step="0.25" />
                              <Field
                                id="hdc-invest-return"
                                label="Annual investment return (%)"
                                value={values.annualInvestmentReturn}
                                onChange={updateField('investment_return', 'annualInvestmentReturn')}
                                step="0.25"
                                hint="Used when the monthly difference between rent and owning is invested."
                              />
                            </div>
                          ) : null}

                          {section.id === 'ownership' ? (
                            <>
                              <div className="rtc-input-grid">
                                <Field
                                  id="hdc-tax"
                                  label="Annual property tax rate (%)"
                                  value={values.propertyTaxRate}
                                  onChange={updateField('property_tax_rate', 'propertyTaxRate')}
                                  step="0.05"
                                  hint="Property tax rates vary widely by state, county, and local assessment rules. Update this with your local estimate."
                                />
                                <Field
                                  id="hdc-pmi"
                                  label="PMI rate (%)"
                                  value={values.pmiRate}
                                  onChange={updateField('pmi_rate', 'pmiRate')}
                                  step="0.05"
                                  hint="PMI commonly applies when the down payment is under 20%. Actual PMI varies by lender, credit profile, loan type, and down payment. PMI is $0 at 20%+ down."
                                />
                                <Field
                                  id="hdc-ins"
                                  label="Annual homeowners insurance rate (%)"
                                  value={values.homeownersInsuranceRate}
                                  onChange={updateField('insurance_rate', 'homeownersInsuranceRate')}
                                  step="0.05"
                                  hint="Insurance varies by state, property type, coverage, and risk factors."
                                />
                                <Field
                                  id="hdc-maint"
                                  label="Annual maintenance reserve (%)"
                                  value={values.maintenanceRate}
                                  onChange={updateField('maintenance_rate', 'maintenanceRate')}
                                  step="0.05"
                                  hint="A common planning assumption is around 1% of home value per year, but actual repairs are uneven."
                                />
                                <Field
                                  id="hdc-close"
                                  label="Buying closing cost rate (%)"
                                  value={values.closingCostRate}
                                  onChange={updateField('closing_cost_rate', 'closingCostRate')}
                                  step="0.25"
                                  hint="The CFPB says closing costs commonly range from 2% to 5% of purchase price, excluding the down payment. This calculator uses an editable estimate."
                                />
                                <Field
                                  id="hdc-sell"
                                  label="Future selling cost rate (%)"
                                  value={values.sellingCostRate}
                                  onChange={updateField('selling_cost_rate', 'sellingCostRate')}
                                  step="0.25"
                                  hint="Selling costs may include agent commissions, transfer taxes, concessions, and other transaction costs."
                                />
                                <Field id="hdc-hoa" label="Monthly HOA fee (optional)" value={values.monthlyHoa} onChange={updateField('monthly_hoa', 'monthlyHoa')} step="25" />
                                <Field
                                  id="hdc-cash"
                                  label="Available cash for housing decision"
                                  value={values.availableCash}
                                  onChange={updateField('available_cash', 'availableCash')}
                                  step="1000"
                                  hint={
                                    closingPreview != null
                                      ? `Defaults to down payment + estimated closing costs (${formatUsd(closingPreview)} closing). If you rent, this cash can be invested instead.`
                                      : 'Defaults to down payment + estimated closing costs.'
                                  }
                                />
                              </div>
                              {projection ? (
                                <div className="hdc-mini-breakdown" aria-label="Upfront cash breakdown">
                                  <div><span>Down payment</span><strong>{formatUsd(projection.upfrontCash.downPayment)}</strong></div>
                                  <div><span>Est. closing costs</span><strong>{formatUsd(projection.upfrontCash.closingCosts)}</strong></div>
                                  <div><span>Total cash to buy</span><strong>{formatUsd(projection.upfrontCash.cashToBuy)}</strong></div>
                                  <div><span>Cash invested if renting</span><strong>{formatUsd(projection.upfrontCash.cashInvestedIfRenting)}</strong></div>
                                </div>
                              ) : null}
                            </>
                          ) : null}

                          {section.id === 'tax' ? (
                            <>
                              <p className="hdc-tax-note">{TAX_DISCLOSURE}</p>
                              <div className="rtc-input-grid">
                                <div className="pete-field">
                                  <label htmlFor="hdc-filing">Filing status</label>
                                  <select
                                    id="hdc-filing"
                                    value={values.filingStatus}
                                    onChange={(e) => updateField('filing_status', 'filingStatus')(e.target.value)}
                                  >
                                    {FILING_STATUS_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                  <p className="pete-hint">
                                    Sets the 2026 standard deduction assumption (
                                    {formatUsd(standardDeductionForStatus(values.filingStatus))}
                                    ).
                                  </p>
                                </div>
                              </div>
                              <div className="hdc-mini-breakdown" aria-label="Built-in tax assumptions">
                                <div>
                                  <span>Federal marginal rate</span>
                                  <strong>{(DEFAULT_TAX_ASSUMPTIONS.federalMarginalRate * 100).toFixed(0)}%</strong>
                                </div>
                                <div>
                                  <span>SALT cap</span>
                                  <strong>{formatUsd(DEFAULT_TAX_ASSUMPTIONS.saltCap)}</strong>
                                </div>
                                <div>
                                  <span>SALT includes</span>
                                  <strong>Property tax only</strong>
                                </div>
                              </div>
                              <p className="hdc-assumption-note">
                                Want a thorough housing + tax scenario with your real numbers? That analysis belongs in a comprehensive financial plan. Start with a Goal-Setting Meeting below.
                              </p>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                <button className="pete-secondary-action" type="button" onClick={resetCalculator}>
                  Reset calculator
                </button>
              </div>
            </div>

            <div className="hdc-results-col">
              {projection ? (
                <>
                  <div className="pete-card hdc-summary-card">
                    <h2>Decision Snapshot</h2>
                    <div className="trc-snapshot-grid hdc-snapshot-grid">
                      <SnapshotCard label="Best projected path" value={projection.bestPathLabel} subtitle={`${formatUsd(projection.bestNetWorthAt30)} at year 30`} accent />
                      <SnapshotCard label="Buying net worth (yr 30)" value={formatUsd(projection.buyNetWorthAt30)} subtitle="After-sale equity + investments + est. tax benefit" />
                      <SnapshotCard label="Rent & invest (yr 30)" value={formatUsd(projection.rentNetWorthAt30)} />
                      <SnapshotCard
                        label="Buy − rent difference"
                        value={`${projection.buyVsRentDeltaAt30 >= 0 ? '+' : '−'}${formatUsd(Math.abs(projection.buyVsRentDeltaAt30))}`}
                        subtitle={projection.buyVsRentDeltaAt30 >= 0 ? 'Buying ahead' : 'Renting ahead'}
                      />
                      <SnapshotCard label="Monthly cost to own" value={formatUsd(projection.monthlyCostToOwn)} subtitle="P&I + tax + PMI + insurance + maintenance + HOA" />
                      <SnapshotCard label="Monthly rent" value={formatUsd(projection.monthlyRent)} />
                      <SnapshotCard label="Break-even year" value={projection.breakEvenYear ? `Year ${projection.breakEvenYear}` : 'None in 30 yrs'} />
                      <SnapshotCard label="Payoff date (standard)" value={formatDateLabel(projection.payoffDate)} subtitle={`Interest ${formatUsd(projection.totalInterest)}`} />
                      <SnapshotCard
                        label="Bi-weekly payoff"
                        value={formatDateLabel(projection.paymentStrategies.biweekly.payoffDate)}
                        subtitle={`Interest saved ${formatUsd(projection.paymentStrategies.biweekly.interestSaved)}`}
                        accent
                      />
                    </div>
                  </div>

                  <div className="pete-card">
                    <h2>What if you paid extra?</h2>
                    <MultiLineChart
                      title={null}
                      subtitle="Net worth over time with no extra inputs — one or two full payments each year, or bi-weekly (26 half-payments ≈ 13 monthly payments per year)."
                      series={projection.chartSeries}
                      lines={[
                        { key: 'standardNetWorth', label: 'Standard', color: '#94a3b8' },
                        { key: 'oneExtraNetWorth', label: '1 extra/yr', color: '#1d4ed8' },
                        { key: 'twoExtraNetWorth', label: '2 extras/yr', color: '#0f766e' },
                        { key: 'biweeklyNetWorth', label: 'Bi-weekly', color: '#b45309' },
                      ]}
                    />
                  </div>

                  <div className="pete-card">
                    <h2>Monthly ownership cost breakdown</h2>
                    <div className="hdc-mini-breakdown hdc-mini-breakdown--grid">
                      <div><span>Principal &amp; interest</span><strong>{formatUsd(projection.ownershipCostBreakdown.principalAndInterest)}</strong></div>
                      <div><span>Property taxes</span><strong>{formatUsd(projection.ownershipCostBreakdown.propertyTax)}</strong></div>
                      <div><span>PMI</span><strong>{formatUsd(projection.ownershipCostBreakdown.pmi)}</strong></div>
                      <div><span>Homeowners insurance</span><strong>{formatUsd(projection.ownershipCostBreakdown.insurance)}</strong></div>
                      <div><span>Maintenance reserve</span><strong>{formatUsd(projection.ownershipCostBreakdown.maintenance)}</strong></div>
                      <div><span>HOA</span><strong>{formatUsd(projection.ownershipCostBreakdown.hoa)}</strong></div>
                      <div className="hdc-mini-breakdown-total"><span>Total monthly cost to own</span><strong>{formatUsd(projection.ownershipCostBreakdown.total)}</strong></div>
                    </div>
                    <CostBreakdownBars breakdown={projection.ownershipCostBreakdown} />

                    <div className="hdc-invest-callout" role="note">
                      <h3>If this difference was invested</h3>
                      <p>
                        Monthly rent is <strong>{formatUsd(projection.cashFlowInvest.monthlyRent)}</strong>.
                        Total cost to own is <strong>{formatUsd(projection.cashFlowInvest.monthlyOwnCost)}</strong>.
                      </p>
                      {projection.cashFlowInvest.investsWhenRenting ? (
                        <p>
                          If you rent instead, about{' '}
                          <strong>{formatUsd(projection.cashFlowInvest.monthlyInvestedIfRenting)}/month</strong>{' '}
                          (the difference) is modeled as invested at your assumed return — on top of investing the
                          down payment and closing-cost cash you did not spend.
                        </p>
                      ) : projection.cashFlowInvest.investsWhenBuying ? (
                        <p>
                          Owning costs less than renting by about{' '}
                          <strong>{formatUsd(projection.cashFlowInvest.monthlyInvestedIfBuying)}/month</strong>.
                          That savings is modeled as invested on the buy path.
                        </p>
                      ) : (
                        <p>Monthly rent and ownership costs are about even under these assumptions.</p>
                      )}
                    </div>
                  </div>

                  <div className="pete-card">
                    <h2>Upfront cash breakdown</h2>
                    <div className="hdc-mini-breakdown hdc-mini-breakdown--grid">
                      <div><span>Down payment</span><strong>{formatUsd(projection.upfrontCash.downPayment)}</strong></div>
                      <div><span>Estimated buying closing costs</span><strong>{formatUsd(projection.upfrontCash.closingCosts)}</strong></div>
                      <div><span>Total cash needed to buy</span><strong>{formatUsd(projection.upfrontCash.cashToBuy)}</strong></div>
                      <div><span>Cash invested if renting instead</span><strong>{formatUsd(projection.upfrontCash.cashInvestedIfRenting)}</strong></div>
                    </div>
                    <p className="hdc-assumption-note">
                      Closing costs are treated as cash used at purchase, not home equity. If you rent, available housing cash (down payment + closing estimate, or your override) can be invested.
                    </p>
                  </div>

                  <div className="hdc-tabs" role="tablist" aria-label="Result views">
                    {[
                      { id: 'results', label: 'Results' },
                      { id: 'charts', label: 'Charts' },
                      { id: 'tables', label: 'Tables' },
                      { id: 'tax', label: 'Tax' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={resultsTab === tab.id}
                        className={`hdc-tab${resultsTab === tab.id ? ' hdc-tab--active' : ''}`}
                        onClick={() => setResultsTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {resultsTab === 'results' ? <ResultsPanel projection={projection} /> : null}
                  {resultsTab === 'charts' ? <ChartsPanel projection={projection} /> : null}
                  {resultsTab === 'tables' ? <TablesPanel projection={projection} /> : null}
                  {resultsTab === 'tax' ? <TaxPanel projection={projection} /> : null}

                  <div className="pete-card hdc-meaning">
                    <h2>What This Means</h2>
                    <div className="rtc-verdict-card">
                      <p className="rtc-verdict-label">Tradeoff framing</p>
                      <p className="rtc-verdict-headline">{projection.interpretation.headline}</p>
                    </div>
                    <ul className="hdc-meaning-list">
                      {projection.interpretation.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <p className="hdc-framing">{projection.interpretation.framing}</p>
                  </div>

                  <div className="pete-disclaimer-banner hdc-disclaimer" role="note">
                    <strong>Reminder.</strong> {LONG_DISCLOSURE}
                  </div>

                  <CalculatorDiscoveryCta
                    className="rtc-cta"
                    eyebrow="NEXT STEP"
                    headline="Want a thorough housing scenario for your situation?"
                    body={[
                      'This calculator uses simplified assumptions. A comprehensive financial plan reviews rent vs buy with your real cash flow, taxes, down payment strategy, liquidity needs, and long-term net worth.',
                      'Start with a Goal-Setting Meeting: bring the result from this page and we’ll talk through your goals and how a plan would approach the decision.',
                    ]}
                    buttonText="Book a Goal-Setting Meeting"
                    buttonId="housing_decision_calculator_financial_checkup_cta"
                    secondaryButtonId="housing_decision_calculator_send_result_cta"
                    calculatorId={CALCULATOR_ID}
                    calculatorName={CALCULATOR_NAME}
                    postButtonLine={LONG_DISCLOSURE}
                  />
                  <ServiceNextStep
                    contentCategory={CONTENT_CATEGORIES.HOUSING}
                    calculatorId={CALCULATOR_ID}
                    calculatorName={CALCULATOR_NAME}
                    promotionalAngle="housing_calculator"
                  />
                </>
              ) : (
                <div className="pete-card">
                  <h2>Results</h2>
                  <p className="hdc-section-lead">
                    Enter valid assumptions to see the comparison. Down payment cannot exceed home value, and
                    closing cost rate should stay within 0–6%.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function ResultsPanel({ projection }) {
  return (
    <div className="pete-card">
      <h2>Down payment comparison — year-30 net worth</h2>
      <p className="hdc-section-lead">
        Available cash is held fixed. Each down-payment option is shown under the standard schedule and the
        extra-payment strategies (1 extra/year, 2 extras/year, and bi-weekly).
      </p>
      <div className="hdc-table-wrap">
        <table className="hdc-table hdc-table--dense">
          <thead>
            <tr>
              <th scope="col">Down payment</th>
              <th scope="col">Loan</th>
              <th scope="col">PMI</th>
              <th scope="col">Cash left</th>
              <th scope="col">Standard</th>
              <th scope="col">1 extra/yr</th>
              <th scope="col">2 extras/yr</th>
              <th scope="col">Bi-weekly</th>
            </tr>
          </thead>
          <tbody>
            {projection.downPaymentOptions.map((opt) => (
              <tr key={opt.label} className={opt === projection.downPaymentLeader ? 'hdc-row-lead' : undefined}>
                <td>{opt.label}</td>
                <td>{formatUsd(opt.loanAmount)}</td>
                <td>{formatUsd(opt.monthlyPmi)}</td>
                <td>{formatUsd(opt.cashLeftInvested)}</td>
                <td>{formatUsd(opt.byStrategy.standard.netWorthAt30)}</td>
                <td>{formatUsd(opt.byStrategy.one_extra.netWorthAt30)}</td>
                <td>{formatUsd(opt.byStrategy.two_extra.netWorthAt30)}</td>
                <td>{formatUsd(opt.byStrategy.biweekly.netWorthAt30)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TaxPanel({ projection }) {
  return (
    <div className="pete-card">
      <h2>Annual tax estimate</h2>
      <p className="hdc-tax-note">{TAX_DISCLOSURE}</p>
      <div className="hdc-table-wrap">
        <table className="hdc-table hdc-table--dense">
          <thead>
            <tr>
              <th scope="col">Year</th>
              <th scope="col">Mortgage interest</th>
              <th scope="col">Property taxes</th>
              <th scope="col">SALT used</th>
              <th scope="col">Itemized</th>
              <th scope="col">Standard</th>
              <th scope="col">Incremental tax benefit</th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            {projection.taxTable.map((row) => (
              <tr key={row.year}>
                <td>{row.year}</td>
                <td>{formatUsd(row.mortgageInterest)}</td>
                <td>{formatUsd(row.propertyTaxes)}</td>
                <td>{formatUsd(row.saltUsed)}</td>
                <td>{formatUsd(row.itemizedDeductions)}</td>
                <td>{formatUsd(row.standardDeduction)}</td>
                <td>{formatUsd(row.incrementalTaxBenefit)}</td>
                <td className="hdc-wrap-cell">{row.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TablesPanel({ projection }) {
  return (
    <div className="pete-card">
      <h2>Rent vs buy by year</h2>
      <p className="hdc-section-lead">
        Buy net worth uses estimated after-sale equity plus side investments plus cumulative incremental tax benefit.
      </p>
      <div className="hdc-table-wrap">
        <table className="hdc-table hdc-table--dense">
          <thead>
            <tr>
              <th scope="col">Year</th>
              <th scope="col">Home value</th>
              <th scope="col">Mortgage bal.</th>
              <th scope="col">Gross equity</th>
              <th scope="col">After-sale equity</th>
              <th scope="col">Rent investments</th>
              <th scope="col">Cum. rent</th>
              <th scope="col">Cum. ownership</th>
              <th scope="col">Est. tax benefit</th>
              <th scope="col">Buy NW</th>
              <th scope="col">Rent NW</th>
              <th scope="col">Difference</th>
            </tr>
          </thead>
          <tbody>
            {projection.yearTable.map((row) => (
              <tr key={row.year}>
                <td>{row.year}</td>
                <td>{formatUsd(row.buy?.homeValue)}</td>
                <td>{formatUsd(row.buy?.endingBalance)}</td>
                <td>{formatUsd(row.buy?.equityAmount)}</td>
                <td>{formatUsd(row.buy?.afterSaleEquity)}</td>
                <td>{formatUsd(row.rent?.investmentValue)}</td>
                <td>{formatUsd(row.rent?.cumulativeRent)}</td>
                <td>{formatUsd(row.buy?.cumulativeOwnershipCosts)}</td>
                <td>{formatUsd(row.cumulativeTaxBenefit)}</td>
                <td>{formatUsd(row.buyNetWorth)}</td>
                <td>{formatUsd(row.rentNetWorth)}</td>
                <td className={row.buyVsRent >= 0 ? 'hdc-delta-pos' : 'hdc-delta-neg'}>
                  {row.buyVsRent >= 0 ? '+' : '−'}
                  {formatUsd(Math.abs(row.buyVsRent))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartsPanel({ projection }) {
  const series = projection.chartSeries;
  return (
    <>
      <div className="pete-card">
        <MultiLineChart
          title="Net worth over time"
          subtitle="Buy (after-sale equity + investments + tax benefit) vs rent and invest available cash."
          series={series}
          lines={[
            { key: 'buyNetWorth', label: 'Buy', color: '#2d5074' },
            { key: 'rentNetWorth', label: 'Rent & invest', color: '#0f766e' },
          ]}
        />
      </div>
      <div className="pete-card">
        <MultiLineChart
          title="After-sale home equity vs invested housing cash"
          subtitle="Owner after-sale equity versus the renter’s invested available cash."
          series={series}
          lines={[
            { key: 'buyAfterSaleEquity', label: 'After-sale equity', color: '#2d5074' },
            { key: 'rentInvested', label: 'Invested if renting', color: '#c2410c' },
          ]}
        />
      </div>
      <div className="pete-card">
        <MultiLineChart
          title="Estimated incremental tax benefit by year"
          subtitle="Only the amount by which itemizing exceeds the standard deduction × marginal rate."
          series={series.filter((row) => row.year > 0)}
          lines={[{ key: 'taxBenefit', label: 'Incremental tax benefit', color: '#0f766e' }]}
        />
      </div>
      <div className="pete-card">
        <DownPaymentBarChart options={projection.downPaymentOptions} />
      </div>
    </>
  );
}

function CostBreakdownBars({ breakdown }) {
  const parts = [
    { key: 'pi', label: 'P&I', value: breakdown.principalAndInterest, color: '#2d5074' },
    { key: 'tax', label: 'Taxes', value: breakdown.propertyTax, color: '#0f766e' },
    { key: 'pmi', label: 'PMI', value: breakdown.pmi, color: '#b45309' },
    { key: 'ins', label: 'Insurance', value: breakdown.insurance, color: '#1d4ed8' },
    { key: 'maint', label: 'Maintenance', value: breakdown.maintenance, color: '#64748b' },
    { key: 'hoa', label: 'HOA', value: breakdown.hoa, color: '#94a3b8' },
  ].filter((part) => part.value > 0);
  const total = parts.reduce((sum, part) => sum + part.value, 0) || 1;

  return (
    <div className="hdc-cost-bars" aria-hidden="true">
      <div className="hdc-cost-bar-track">
        {parts.map((part) => (
          <span
            key={part.key}
            style={{ width: `${(part.value / total) * 100}%`, background: part.color }}
            title={`${part.label}: ${formatUsd(part.value)}`}
          />
        ))}
      </div>
      <div className="hdc-chart-legend">
        {parts.map((part) => (
          <span key={part.key}>
            <i style={{ background: part.color }} />
            {part.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function MultiLineChart({ title, subtitle, series, lines }) {
  const width = 640;
  const height = 280;
  const pad = { top: 24, right: 16, bottom: 40, left: 56 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const years = series.map((row) => row.year);
  const yMax = Math.max(
    1,
    ...lines.flatMap((line) => series.map((row) => Number(row[line.key]) || 0)),
  ) * 1.08;
  const x = (year) => pad.left + ((year - years[0]) / (years[years.length - 1] - years[0] || 1)) * plotWidth;
  const y = (value) => pad.top + plotHeight - (value / (yMax || 1)) * plotHeight;
  const buildPath = (key) => series
    .filter((row) => row[key] != null && Number.isFinite(row[key]))
    .map((row, index) => `${index === 0 ? 'M' : 'L'} ${x(row.year).toFixed(1)} ${y(row[key]).toFixed(1)}`)
    .join(' ');
  const ariaLabel = title || 'Net worth over time by payment strategy';

  return (
    <div className="hdc-chart-wrap">
      {title ? <h3>{title}</h3> : null}
      {subtitle ? <p>{subtitle}</p> : null}
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel} className="hdc-chart-svg">
        {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
          const value = yMax * tick;
          return (
            <g key={tick}>
              <line className="hdc-chart-grid" x1={pad.left} x2={width - pad.right} y1={y(value)} y2={y(value)} />
              <text className="hdc-chart-axis" x={pad.left - 8} y={y(value) + 4} textAnchor="end">
                {formatUsdCompact(value)}
              </text>
            </g>
          );
        })}
        {years.map((year) => (
          <text key={year} className="hdc-chart-axis" x={x(year)} y={height - 14} textAnchor="middle">{year}</text>
        ))}
        {lines.map((line) => (
          <path key={line.key} d={buildPath(line.key)} fill="none" stroke={line.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        ))}
      </svg>
      <div className="hdc-chart-legend">
        {lines.map((line) => (
          <span key={line.key}>
            <i style={{ background: line.color }} />
            {line.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function DownPaymentBarChart({ options }) {
  const width = 640;
  const height = 280;
  const pad = { top: 28, right: 16, bottom: 52, left: 56 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const strategies = [
    { id: 'standard', color: '#94a3b8', label: 'Standard' },
    { id: 'one_extra', color: '#1d4ed8', label: '1 extra' },
    { id: 'two_extra', color: '#0f766e', label: '2 extras' },
    { id: 'biweekly', color: '#b45309', label: 'Bi-weekly' },
  ];
  const maxNw = Math.max(
    1,
    ...options.flatMap((opt) => strategies.map((s) => opt.byStrategy[s.id]?.netWorthAt30 || 0)),
  );
  const groupWidth = plotWidth / options.length;

  return (
    <div className="hdc-chart-wrap">
      <h3>Down payment comparison by year-30 net worth</h3>
      <p>Each down payment option under standard, 1 extra/year, 2 extras/year, and bi-weekly payments.</p>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Down payment comparison chart" className="hdc-chart-svg">
        {options.map((opt, index) => {
          const groupX = pad.left + index * groupWidth;
          const barW = groupWidth / (strategies.length + 1);
          return (
            <g key={opt.label}>
              {strategies.map((strategy, sIndex) => {
                const value = opt.byStrategy[strategy.id]?.netWorthAt30 || 0;
                const barHeight = (value / maxNw) * plotHeight;
                const x = groupX + barW * 0.5 + sIndex * barW;
                const y = pad.top + plotHeight - barHeight;
                return (
                  <rect key={strategy.id} x={x} y={y} width={barW * 0.85} height={barHeight} rx="3" fill={strategy.color} />
                );
              })}
              <text className="hdc-chart-axis" x={groupX + groupWidth / 2} y={height - 18} textAnchor="middle">
                {opt.label.replace(' down', '')}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="hdc-chart-legend">
        {strategies.map((strategy) => (
          <span key={strategy.id}>
            <i style={{ background: strategy.color }} />
            {strategy.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Field({ id, label, value, onChange, step = '1', hint, type = 'number' }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        inputMode={type === 'number' ? 'decimal' : undefined}
        min={type === 'number' ? '0' : undefined}
        step={type === 'number' ? step : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? <p className="pete-hint">{hint}</p> : null}
    </div>
  );
}

function SnapshotCard({ label, value, subtitle, accent = false }) {
  return (
    <div className={`trc-snapshot-card${accent ? ' trc-snapshot-card--accent' : ''}`}>
      <div className="trc-snapshot-label">{label}</div>
      <div className="trc-snapshot-value">{value}</div>
      {subtitle ? <p className="trc-snapshot-sub">{subtitle}</p> : null}
    </div>
  );
}
